import AdvancedSearch from '@app/components/AdvancedSearch'
import Image from '@app/components/Image'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import { IDevelopmentSite } from '@app/models/DevelopmentSite'
import { ImageSize } from '@app/models/enums'
import {
  IGlobalConfigurationState,
  IGlobalDevelopmentProjectClients,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { humanize } from '@app/utils/formatting'
import * as React from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { Column } from 'react-virtualized'
import { Col, FormGroup, Input, Label } from 'reactstrap'
import { actionCreators, ISiteListState } from '../modules/siteList'

interface IComponentRouteProps {
  slug: string
}

type IComponentProps = ISiteListState &
  IGlobalConfigurationState &
  IGlobalDevelopmentProjectClients &
  typeof actionCreators &
  RouteComponentProps<IComponentRouteProps>

class SiteList extends React.PureComponent<IComponentProps> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  public constructor(props: any) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public render() {
    return (
      <div className='container d-flex flex-column h-100 pt-3'>
        <div>
          <AdvancedSearch className='mt-1' expand='md'>
            <Col md={4} xl={3}>
              <FormGroup>
                <Label>City</Label>
                <Input
                  type='select'
                  bsSize='sm'
                  value={this.props.city}
                  onChange={this._onChangeCity}
                >
                  <option key='client1' value=''>
                    All
                  </option>
                  {this.props.developmentSiteMetadata.cities
                    .map((c: any, i) => (
                      <option key={`client-${i}`} value={c}>
                        {c}
                      </option>
                    ))
                    .toJS()}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4} xl={3}>
              <FormGroup>
                <Label>Status</Label>
                <Input
                  type='select'
                  bsSize='sm'
                  value={this.props.status}
                  onChange={this._onChangeStatus}
                >
                  <option key='status1' value=''>
                    All
                  </option>
                  {this.props.configuration.developmentSiteStatuses
                    .map((c, i) => (
                      <option key={`status-${i}`} value={c}>
                        {humanize(c)}
                      </option>
                    ))
                    .toJS()}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4} xl={3}>
              <FormGroup>
                <Label>Sort</Label>
                <Input
                  type='select'
                  bsSize='sm'
                  value={this.props.sortOrder}
                  onChange={this._onChangeSort}
                >
                  {this.props.configuration.developmentSiteSortOptions
                    .map((c, i) => (
                      <option key={`sort-${i}`} value={c}>
                        {humanize(c)}
                      </option>
                    ))
                    .toJS()}
                </Input>
              </FormGroup>
            </Col>
          </AdvancedSearch>
        </div>
        <InfiniteScrollTable
          isLoading={this.props.isLoading}
          items={this.props.items}
          rowHeight={112}
          loadItems={this._loadItems}
          noRowsRenderer={this._noRowsRenderer}
          onRowClicked={this._onRowClicked}
          totalItems={this.props.totalItems}
          ref={this.tableRef}
        >
          <Column
            cellRenderer={({ rowData }) => {
              const site = rowData as IDevelopmentSite

              return (
                <div>
                  <div style={{ width: '200px' }}>
                    <Image
                      defaultImageUrl='/images/default-property.png'
                      image={site.coverPhoto}
                      size={ImageSize.h200w250}
                      width={200}
                    />
                  </div>
                </div>
              )
            }}
            className='d-none d-md-block'
            dataKey='name'
            style={{ marginLeft: 0 }}
            width={200}
          />
          <Column
            cellRenderer={({ rowData }) => {
              const site = rowData as IDevelopmentSite

              return (
                <div>
                  <div style={{ fontWeight: 'bold' }}>{site.name}</div>
                  <div>{site.address.line1}</div>
                  <div>
                    {`${site.address.city}, ${site.address.state} ${
                      site.address.zip
                    }`}
                  </div>
                </div>
              )
            }}
            dataKey='name'
            flexGrow={1}
            width={700}
          />
          <Column
            cellRenderer={({ rowData }) => {
              const site = rowData as IDevelopmentSite

              return (
                <div
                  className={`development-site-status-container ${site.status
                    .toString()
                    .toLowerCase()}`}
                >
                  <div className='status-label'>{site.status}</div>
                </div>
              )
            }}
            dataKey='name'
            style={{ marginRight: 0 }}
            flexGrow={1}
            width={100}
          />
        </InfiniteScrollTable>
      </div>
    )
  }

  private _onChangeCity(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setCity(event.target.value)
    this._reloadData()
  }

  private _onChangeSort(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSort(event.target.value)
    this._reloadData()
  }

  private _onChangeStatus(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setStatus(event.target.value)
    this._reloadData()
  }

  private _loadItems(
    startIndex: number,
    stopIndex: number,
    append: boolean,
  ): Promise<void> {
    return new Promise((resolve) => {
      this.props.loadSites(
        startIndex,
        stopIndex,
        append,
        resolve,
        this.props.match.params.slug,
      )
    })
  }

  private _noRowsRenderer() {
    return <div>No sites</div>
  }

  private _onRowClicked(item: IDevelopmentSite, index: number) {
    this.props.history.push(
      `/development/client/${this.props.match.params.slug}/site/${item.slug}`,
    )
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}

export default withRouter(SiteList)
